import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { SquareButton, SquareButtonKind } from "./buttons/SquareButton";
import { SquareIconButton } from "./buttons/SquareIconButton";

import { t } from "i18n/i18n";
import {
  CircleCheckIcon,
  CircleCrossIcon,
  CircleExclamationIcon,
  CircleInfoIcon,
  CrossRemoveIcon,
} from "mds/icons";
import type { IconProps } from "mds/utils/images";
import { AmplitudeEventPropertiesType, TrackingProps } from "utils/amplitude";

type Color = "red" | "orange" | "blue" | "green";

type Kind = "info" | "warning" | "error" | "success";

const KIND_TO_ICON: Record<Kind, FC<IconProps>> = {
  info: CircleInfoIcon,
  warning: CircleExclamationIcon,
  error: CircleCrossIcon,
  success: CircleCheckIcon,
};

const KIND_TO_COLOR: Record<Kind, Color> = {
  info: "blue",
  warning: "orange",
  error: "red",
  success: "green",
};

const KIND_TO_ACTION_BUTTON_KIND: Record<Kind, SquareButtonKind> = {
  info: "accent",
  warning: "warning",
  error: "destructive",
  success: "success",
};

const COLOR_TO_CLASSES: Record<Color, string> = {
  red: "bg-red-tint-93 border border-red-tint-70",
  orange: "bg-orange-tint-93 border border-orange-tint-70",
  blue: "bg-blue-tint-93 border border-blue-tint-70",
  green: "bg-green-tint-93 border border-green-tint-70",
};

export type BannerProps = TrackingProps & {
  kind: Kind;
  onDismiss?: () => void;
  onAction?: () => void;
  actionButtonText?: string;
  /** NOTE: if you use the buttonChildren prop, make sure the primary action button uses the appropriate kind and size="s"
   *  to match the default button in here. If there are additional buttons, they should use kind="tertiary" and size="s"
   */
  buttonChildren?: ReactNode;
  closeEventName?: string;
  closeEventProperties?: Partial<AmplitudeEventPropertiesType>;
  id?: string;
  className?: string;
  children: ReactNode;
  Icon?: FC<IconProps>;
};

/**
 * A Banner (Alert) component with optional pre-styled icons and actions buttons.
 */
export const Banner: FC<BannerProps> = ({
  kind,
  id,
  className,
  children,
  onDismiss,
  onAction,
  buttonChildren,
  Icon,
  actionButtonText = "Take Action",
  closeEventName,
  closeEventProperties,
  ...props
}) => {
  const BannerIcon = Icon || KIND_TO_ICON[kind];
  const color = KIND_TO_COLOR[kind];
  return (
    <div
      className={twMerge(
        "flex min-h-[44px] flex-none items-center justify-between gap-2 rounded-lg p-1.5 pl-4 text-sm text-black-tint-20",
        color && COLOR_TO_CLASSES[color],
        className,
      )}
      id={id}
    >
      <div className="flex items-center gap-2 p-1">
        {BannerIcon && <BannerIcon className={`text-${color}`} />}
        {children}
      </div>

      <div className="flex items-center gap-2">
        {buttonChildren}

        {onAction && (
          <SquareButton
            kind={KIND_TO_ACTION_BUTTON_KIND[kind]}
            size="s"
            onClick={onAction}
            {...props}
          >
            {actionButtonText}
          </SquareButton>
        )}

        {onDismiss && (
          <SquareIconButton
            eventName={closeEventName}
            eventProps={closeEventProperties}
            kind="tertiary"
            size="s"
            title={t("common.dismiss")}
            onClick={onDismiss}
          >
            <CrossRemoveIcon />
          </SquareIconButton>
        )}
      </div>
    </div>
  );
};
